import React, { useState, useEffect } from "react";
import { Folder as FolderEntity } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useData } from "../DataContext";
import type { Folder, FolderColor } from "@/types";

interface ColorOption {
  value: FolderColor;
  label: string;
  class: string;
}

const colorOptions: ColorOption[] = [
  { value: "blue", label: "Blue", class: "bg-blue-500" },
  { value: "green", label: "Green", class: "bg-green-500" },
  { value: "purple", label: "Purple", class: "bg-purple-500" },
  { value: "orange", label: "Orange", class: "bg-orange-500" },
  { value: "red", label: "Red", class: "bg-red-500" },
  { value: "teal", label: "Teal", class: "bg-teal-500" },
];

interface FolderDialogProps {
  open: boolean;
  folder: Folder | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function FolderDialog({ open, folder, onClose, onSaved }: FolderDialogProps) {
  const { folders, lists, sections, listItems, runOptimisticUpdate, setOptimisticData } = useData();
  const [name, setName] = useState("");
  const [color, setColor] = useState<FolderColor>("blue");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (folder) {
        setName(folder.name);
        setColor(folder.color || "blue");
      } else {
        setName("");
        setColor("blue");
      }
    }
  }, [folder, open]);

  const handleSave = async () => {
    if (!name.trim()) return;

    setIsSaving(true);
    const originalState = { folders: [...folders] };
    
    try {
      if (folder) {
        // Update existing folder
        const task = async () => FolderEntity.update(folder.id, { name: name.trim(), color });
        const newState = {
          folders: folders.map(f => 
            f.id === folder.id ? { ...f, name: name.trim(), color } : f
          ),
          lists: [...lists],
          sections: [...sections],
          listItems: [...listItems]
        };
        await runOptimisticUpdate(task, { update: newState, originalState: { ...originalState, lists, sections, listItems } });
      } else {
        // Create new folder
        const tempId = `temp-${Date.now()}`;
        const newFolderData = { name: name.trim(), color, position: folders.length };
        
        const task = async () => {
          const createdFolder = await FolderEntity.create(newFolderData);
          setOptimisticData(prevData => ({
            ...prevData,
            folders: prevData.folders.map(f => 
              f.id === tempId ? { ...f, ...createdFolder } : f
            )
          }));
          return createdFolder;
        };
        
        const newState = {
          folders: [...folders, { id: tempId, ...newFolderData } as Folder],
          lists: [...lists],
          sections: [...sections],
          listItems: [...listItems]
        };
        
        await runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, listItems } });
      }
      onSaved();
    } catch (error) {
      console.error("Error saving folder:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const title = folder ? "Edit Folder" : "Create New Folder";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name" className="text-sm font-medium text-slate-700">
              Folder Name *
            </Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter folder name..."
              className="h-12 border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Color</Label>
            <Select value={color} onValueChange={(val) => setColor(val as FolderColor)}>
              <SelectTrigger className="h-12 border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {colorOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full ${option.class}`} />
                      {option.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-200 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? 'Saving...' : 'Save Folder'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
